import React, { useState } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Stack,
  Divider,
  useMediaQuery,
  createTheme,
  ThemeProvider,
  CssBaseline
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PrintIcon from '@mui/icons-material/Print';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import BadgeIcon from '@mui/icons-material/Badge';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PersonIcon from '@mui/icons-material/Person';
import SchoolIcon from '@mui/icons-material/School';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import ListAltIcon from '@mui/icons-material/ListAlt';

// Admin Theme Config
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: '#1a237e' }, // Corporate Indigo Blue
    background: { default: '#f0f2f5' }
  },
  typography: {
    fontFamily: ['Noto Sans Devanagari', 'Mangal', 'sans-serif'].join(',')
  }
});

const statusColor = (remark) =>
  remark === 'पास झाला' ? 'success' : remark === 'चालू आहे' ? 'primary' : 'error';

// Mobile Card View - प्रत्येक विद्यार्थ्यासाठी एक compact कार्ड (icons सोबत)
const StudentCard = ({ student }) => (
  <Paper
    variant="outlined"
    sx={{
      p: 2,
      mb: 2,
      borderRadius: 3,
      borderLeft: '4px solid #1a237e',
    }}
  >
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <PersonIcon sx={{ color: 'primary.main', fontSize: 22 }} />
        <Typography sx={{ fontWeight: 700, fontSize: '0.98rem', color: '#1a1a2e' }}>
          {student.name}
        </Typography>
      </Box>
      <Chip
        label={student.remark}
        size="small"
        color={statusColor(student.remark)}
        sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}
      />
    </Box>

    <Divider sx={{ mb: 1.2 }} />

    <Stack spacing={0.9} sx={{ mb: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <BadgeIcon sx={{ fontSize: 17, color: 'text.secondary' }} />
        <Typography variant="body2" color="text.secondary">रजिस्टर नं:</Typography>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>{student.registerNo}</Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <AssignmentIcon sx={{ fontSize: 17, color: 'text.secondary' }} />
        <Typography variant="body2" color="text.secondary">दाखल क्रमांक:</Typography>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{student.dakhalNo}</Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <SchoolIcon sx={{ fontSize: 17, color: 'text.secondary' }} />
        <Typography variant="body2" color="text.secondary">इयत्ता/तुकडी:</Typography>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{student.class}</Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <EventBusyIcon sx={{ fontSize: 17, color: 'text.secondary' }} />
        <Typography variant="body2" color="text.secondary">सोडल्याचा दिनांक:</Typography>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{student.date}</Typography>
      </Box>
    </Stack>

    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, borderTop: '1px solid #eee', pt: 1 }}>
      <IconButton size="small" color="primary" title="पहा (View)">
        <VisibilityIcon fontSize="small" />
      </IconButton>
      <IconButton size="small" sx={{ color: '#f57c00' }} title="बदला (Edit)">
        <EditIcon fontSize="small" />
      </IconButton>
      <IconButton size="small" sx={{ color: '#333' }} title="प्रिंट (Print)">
        <PrintIcon fontSize="small" />
      </IconButton>
    </Box>
  </Paper>
);

const RegistrationList = () => {
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Master Table State (फक्त महत्त्वाचा इम्पॉर्टंट डेटा फिल्टर करून घेतलेला)
  const [students] = useState([
    { id: 1, registerNo: '10007', dakhalNo: '2725', name: 'तुषार तुका पाटील', class: '12 वी कला', date: '31/05/2024', remark: 'पास झाला' },
    { id: 2, registerNo: '10008', dakhalNo: '2726', name: 'अमित रमेश शिंदे', class: '12 वी विज्ञान', date: '15/06/2025', remark: 'चालू आहे' },
    { id: 3, registerNo: '10009', dakhalNo: '2727', name: 'पूजा विलास कदम', class: '11 वी कला', date: '10/08/2025', remark: 'नाव कमी केले' }
  ]);

  const [searchTerm, setSearchTerm] = useState('');

  // Live Search Filter Logic (नाव, रजिस्टर किंवा दाखल क्रमांकावरून शोधण्यासाठी)
  const filteredStudents = students.filter(student =>
    student.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.registerNo.includes(searchTerm) ||
    student.dakhalNo.includes(searchTerm)
  );

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Container maxWidth="lg" sx={{ my: { xs: 2, md: 4 }, px: { xs: 1.5, sm: 2, md: 3 } }}>

        {/* Header Block */}
        <Box sx={{ mb: { xs: 2, md: 3 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ListAltIcon sx={{ color: 'primary.main', fontSize: { xs: 24, md: 28 } }} />
            <Typography
              variant="h5"
              sx={{
                fontWeight: 'bold',
                color: 'primary.main',
                fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.5rem' },
              }}
            >
              विद्यार्थी नोंदणी मास्टर लिस्ट (Registration Ledger)
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary">
            एकूण नोंदणी रेकॉर्ड्स: <b>{filteredStudents.length} प्रविष्टी (Entries)</b>
          </Typography>
        </Box>

        {/* Control Box & Table Card */}
        <Paper sx={{ p: { xs: 1.5, sm: 2.5, md: 3 }, borderRadius: { xs: 3, md: 4 }, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>

          {/* Live Search Input Bar */}
          <Box sx={{ mb: { xs: 2, md: 3 } }}>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              placeholder="नाव, रजिस्टर नंबर किंवा दाखल क्रमांक शोधा..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ maxWidth: { xs: '100%', sm: 500 } }}
            />
          </Box>

          {/* MOBILE: Card List View */}
          {isMobile ? (
            <Box>
              {filteredStudents.length > 0 ? (
                filteredStudents.map((student) => (
                  <StudentCard key={student.id} student={student} />
                ))
              ) : (
                <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                  <Typography variant="body2">शोधलेला डेटा रेकॉर्डमध्ये सापडला नाही.</Typography>
                </Box>
              )}
            </Box>
          ) : (
            /* DESKTOP / TABLET: Table View */
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, overflowX: 'auto' }}>
              <Table sx={{ minWidth: 720 }}>
                <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>रजिस्टर नं.</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>दाखल क्रमांक</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>विद्यार्थ्याचे संपूर्ण नाव</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>इयत्ता/तुकडी</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>शाळा सोडल्याचा दिनांक</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>शेरा (Status)</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>ॲक्शन्स (Actions)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredStudents.map((student) => (
                    <TableRow key={student.id} hover>
                      <TableCell sx={{ fontWeight: 'bold' }}>{student.registerNo}</TableCell>
                      <TableCell>{student.dakhalNo}</TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>{student.name}</TableCell>
                      <TableCell>{student.class}</TableCell>
                      <TableCell>{student.date}</TableCell>
                      <TableCell>
                        <Chip
                          label={student.remark}
                          size="small"
                          color={statusColor(student.remark)}
                          sx={{ fontWeight: 'bold' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                          <IconButton size="small" color="primary" title="पहा (View)">
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" sx={{ color: '#f57c00' }} title="बदला (Edit)">
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" sx={{ color: '#333' }} title="प्रिंट (Print)">
                            <PrintIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                  {filteredStudents.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
                        शोधलेला डेटा रेकॉर्डमध्ये सापडला नाही.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

        </Paper>
      </Container>
    </ThemeProvider>
  );
};

export default RegistrationList;